import { REGIONAL_LAWN_COSTS, type RegionalCosts } from '@/data/lawn-care-costs'

/**
 * Range computation for the /costs hub.
 *
 * Every dollar figure the hub publishes is derived here from
 * `REGIONAL_LAWN_COSTS` rather than typed into the page. That is not a style
 * preference: a cost page whose numbers are copied by hand goes stale silently
 * and then argues confidently from stale figures. Deriving them means the page
 * and the calculators can never disagree.
 *
 * The one trap this module exists to prevent: `REGIONAL_LAWN_COSTS` MIXES
 * UNITS across its fields. `treatments.*`, `fertilizer.*`, and `seed.*` are all
 * dollars per 1,000 sq ft, `maintenance.mowingPerVisit` is dollars per visit,
 * `waterPerMonth` is dollars per month, and `laborRate` is dollars per hour.
 * A shared formatter that assumes one unit publishes wrong-looking numbers, so
 * `CostUnit` travels with every range and every caller has to name it.
 */

export type CostUnit = 'per1000SqFt' | 'perVisit' | 'perMonth' | 'perHour'

export const UNIT_LABELS: Record<CostUnit, string> = {
  per1000SqFt: 'per 1,000 sq ft',
  perVisit: 'per visit',
  perMonth: 'per month',
  perHour: 'per hour',
}

export interface CostRange {
  low: number
  high: number
  /** Cheapest region in the dataset for this field, for "where" context. */
  lowRegion: string
  /** Priciest region in the dataset for this field. */
  highRegion: string
}

/**
 * Low and high for one cost field across every region in the dataset.
 *
 * Takes a selector rather than a dotted string path so a typo is a type error
 * instead of a runtime `undefined` that formats as "$NaN".
 */
export function rangeAcrossRegions(select: (costs: RegionalCosts) => number): CostRange {
  const regions = Object.values(REGIONAL_LAWN_COSTS)
  if (regions.length === 0) {
    throw new Error('REGIONAL_LAWN_COSTS is empty; cost ranges cannot be computed')
  }

  let low = Number.POSITIVE_INFINITY
  let high = Number.NEGATIVE_INFINITY
  let lowRegion = ''
  let highRegion = ''

  for (const costs of regions) {
    const value = select(costs)
    if (!Number.isFinite(value)) continue
    if (value < low) {
      low = value
      lowRegion = costs.region
    }
    if (value > high) {
      high = value
      highRegion = costs.region
    }
  }

  if (!Number.isFinite(low) || !Number.isFinite(high)) {
    throw new Error('Cost selector returned no finite values across regions')
  }

  return { low, high, lowRegion, highRegion }
}

/**
 * Widest span covering several ranges, for a row that deliberately reports one
 * band across related fields (basic through organic fertilizer, for instance)
 * rather than a separate row per grade.
 */
export function spanRanges(...ranges: CostRange[]): CostRange {
  if (ranges.length === 0) throw new Error('spanRanges requires at least one range')
  return ranges.reduce((acc, r) => ({
    low: r.low < acc.low ? r.low : acc.low,
    high: r.high > acc.high ? r.high : acc.high,
    lowRegion: r.low < acc.low ? r.lowRegion : acc.lowRegion,
    highRegion: r.high > acc.high ? r.highRegion : acc.highRegion,
  }))
}

const money = (value: number) =>
  value % 1 === 0 ? `$${value}` : `$${value.toFixed(2)}`

/** "$9 to $16" for a spread, "$9" when every region agrees. */
export function formatRange(range: CostRange): string {
  return range.low === range.high
    ? money(range.low)
    : `${money(range.low)} to ${money(range.high)}`
}

/** "$9 to $16 per 1,000 sq ft" — the unit is never optional. */
export function formatRangeWithUnit(range: CostRange, unit: CostUnit): string {
  return `${formatRange(range)} ${UNIT_LABELS[unit]}`
}

/**
 * Scale a per-1,000-sq-ft range to a whole lawn. Straight multiplication and
 * nothing more, which is exactly why callers must label the result as
 * arithmetic: a real quote carries a minimum charge and a trip fee that this
 * cannot know about.
 *
 * Throws on any other unit, because scaling a per-visit mowing price by lawn
 * area is the specific wrong number this module exists to stop shipping.
 */
export function scaleToLawn(range: CostRange, unit: CostUnit, squareFeet: number): CostRange {
  if (unit !== 'per1000SqFt') {
    throw new Error(`scaleToLawn requires a per-1,000-sq-ft range, received "${unit}"`)
  }
  const factor = squareFeet / 1000
  return {
    ...range,
    low: Math.round(range.low * factor),
    high: Math.round(range.high * factor),
  }
}

/** Reference lawn used for the whole-lawn column on /costs. */
export const REFERENCE_LAWN_SQ_FT = 5000

export const COST_REGION_COUNT = Object.keys(REGIONAL_LAWN_COSTS).length

export interface ServiceCost {
  id: string
  /** Question-form H2 that mirrors how the query is actually spoken. */
  question: string
  /** Short label for the table row. */
  label: string
  unit: CostUnit
  range: CostRange
  /** What the price covers, and what it does not. */
  note: string
  /** Matching calculator, with a specific CTA label (never "try the tool"). */
  tool: { href: string; label: string }
  /** Matching Angi quote vertical, when one exists for the service. */
  quote?: { href: string; label: string }
  /** Matching long-form Directus article, when one exists. */
  article?: { href: string; label: string }
}

/**
 * The services the hub covers, each one wired to its calculator, its quote
 * vertical, and its long-form article. Ranges are computed, never typed.
 */
export const SERVICE_COSTS: ServiceCost[] = [
  {
    id: 'aeration',
    question: 'How much does lawn aeration cost?',
    label: 'Core aeration',
    unit: 'per1000SqFt',
    range: rangeAcrossRegions((c) => c.treatments.aeration),
    note: 'A professional core aeration pass. Renting a core aerator instead trades the labor for a day rate plus your afternoon.',
    tool: { href: '/tools/aeration-calculator', label: 'Estimate my aeration cost' },
    quote: { href: '/aeration-overseeding-quotes', label: 'Get aeration quotes near me' },
    article: {
      href: '/blog/when-to-aerate-lawn-timing-guide',
      label: 'When to aerate your lawn',
    },
  },
  {
    id: 'overseeding',
    question: 'How much does overseeding cost?',
    label: 'Overseeding',
    unit: 'per1000SqFt',
    range: rangeAcrossRegions((c) => c.treatments.overseeding),
    note: 'Includes the seed, which is why it runs above aeration. Seed price swings with the species you are sowing.',
    tool: { href: '/tools/overseeding-calculator', label: 'Work out my seed quantity' },
    quote: { href: '/aeration-overseeding-quotes', label: 'Get overseeding quotes near me' },
    article: {
      href: '/blog/overseeding-best-practices',
      label: 'How to overseed your lawn',
    },
  },
  {
    id: 'weed-control',
    question: 'How much does lawn weed control cost?',
    label: 'Weed control application',
    unit: 'per1000SqFt',
    range: rangeAcrossRegions((c) => c.treatments.weedControl),
    note: 'One pre-emergent or post-emergent application. A full season is usually several passes, not one.',
    tool: { href: '/tools/herbicide-timing', label: 'Find my herbicide timing window' },
    quote: { href: '/weed-control-quotes', label: 'Get weed control quotes near me' },
    article: {
      href: '/blog/pre-emergent-timing-when-to-apply-guide',
      label: 'Pre-emergent timing by ZIP code',
    },
  },
  {
    id: 'fertilizer',
    question: 'How much does lawn fertilizer cost?',
    label: 'Fertilizer, basic to organic',
    unit: 'per1000SqFt',
    range: spanRanges(
      rangeAcrossRegions((c) => c.fertilizer.basic),
      rangeAcrossRegions((c) => c.fertilizer.organic),
    ),
    note: 'The low end is a basic NPK product and the high end is organic. The rate you need comes off the bag label, not from us.',
    tool: { href: '/tools/fertilizer-calculator', label: 'Calculate my fertilizer rate' },
    quote: { href: '/lawn-care-quotes', label: 'Get lawn treatment quotes near me' },
    article: {
      href: '/blog/how-to-calculate-fertilizer-for-lawn',
      label: 'How to calculate fertilizer for your lawn',
    },
  },
  {
    id: 'dethatching',
    question: 'How much does dethatching cost?',
    label: 'Dethatching or power raking',
    unit: 'per1000SqFt',
    range: rangeAcrossRegions((c) => c.treatments.dethatching),
    note: 'Only worth doing when the thatch layer is genuinely too deep. Most lawns do not need it annually.',
    tool: { href: '/tools/dethatching-calculator', label: 'Check if my lawn needs dethatching' },
    article: {
      href: '/blog/how-to-aerate-your-lawn-the-right-way',
      label: 'Aeration and thatch, done right',
    },
  },
  {
    id: 'lime',
    question: 'How much does a lime application cost?',
    label: 'Lime application',
    unit: 'per1000SqFt',
    range: rangeAcrossRegions((c) => c.treatments.limeApplication),
    note: 'Only apply lime after a soil test says your pH is low. Liming a lawn that does not need it makes things worse.',
    tool: { href: '/tools/lime-calculator', label: 'Calculate my lime requirement' },
    article: {
      href: '/blog/how-to-improve-soil-ph-for-grass',
      label: 'How to improve soil pH for grass',
    },
  },
  {
    id: 'seed',
    question: 'How much does grass seed cost?',
    label: 'Grass seed, cool to warm season',
    unit: 'per1000SqFt',
    range: spanRanges(
      rangeAcrossRegions((c) => c.seed.coolSeason),
      rangeAcrossRegions((c) => c.seed.warmSeason),
      rangeAcrossRegions((c) => c.seed.premium),
    ),
    note: 'Seed for the species that suits your region sits at the low end. Premium blends sit at the top.',
    tool: { href: '/tools/seeding-calculator', label: 'Work out how much seed I need' },
    article: {
      href: '/blog/is-it-too-late-to-seed-lawn-deadlines',
      label: 'Seeding deadlines by region',
    },
  },
  {
    id: 'mowing',
    question: 'How much does lawn mowing cost per visit?',
    label: 'Professional mowing',
    unit: 'perVisit',
    range: rangeAcrossRegions((c) => c.maintenance.mowingPerVisit),
    note: 'Priced per visit for an average lawn rather than by area, so a much larger yard will quote above this.',
    tool: { href: '/tools/mowing-calculator', label: 'Find my mowing height and frequency' },
    quote: { href: '/lawn-care-quotes', label: 'Get mowing quotes near me' },
    article: {
      href: '/blog/mowing-height-by-grass-type-guide',
      label: 'Mowing height by grass type',
    },
  },
  {
    id: 'labor',
    question: 'What do lawn care companies charge per hour?',
    label: 'Crew labor rate',
    unit: 'perHour',
    range: rangeAcrossRegions((c) => c.laborRate),
    note: 'The hourly rate underneath every quoted service, which is why the same job costs different amounts in different regions.',
    tool: { href: '/tools/lawn-cost-estimator', label: 'Estimate my full lawn care cost' },
    quote: { href: '/lawn-care-quotes', label: 'Compare local lawn care quotes' },
    article: {
      href: '/blog/how-to-choose-between-diy-and-professional-lawn-care',
      label: 'DIY versus professional lawn care',
    },
  },
]
